import {
  Button,
  HStack,
  ModalBody,
  ModalContent,
  TabList,
  Tabs,
  Text
} from '@chakra-ui/react'
import { Person } from '@material-ui/icons'
import { Form, Formik, type FormikProps } from 'formik'
import moment from 'moment'
import React from 'react'
import * as Yup from 'yup'
import { useServiceTypeControllerGetDefaultServiceType } from '~/clinician-api'
import { useCalendarEvent } from '../../context/calendar-event.context'
import {
  EVENT_SETTING_OPTIONS,
  type EventSetting,
  EventType
} from '../../models'
import { FancyTab } from '../fancy-tab'
import { ClientSessionForm } from './client-session-form'
import { EventModalFooter } from './event-modal-footer'
import { OtherEventForm } from './other-event-form'

interface AddEditEventModalProps {
  onSubmit: (values: AddEditEventFormValues) => void
  onCancel: () => void
  onEditClient: () => void
  onSwitchEvent: (eventType: EventType) => void
}

export interface AddEditEventFormValues {
  eventType: EventType
  patientId?: string
  subject: string
  serviceType: { value: string; label: string } | null
  eventDate: Date
  eventStartTime: string
  eventEndTime: string
  setting: EventSetting
  recurring: string
  occurrenceCount: number
  reminders: boolean
  description: string
}

export const AddEditEventModal: React.FC<AddEditEventModalProps> = ({
  onSubmit,
  onCancel,
  onEditClient,
  onSwitchEvent
}) => {
  const { currentEvent, serviceTypeOptions, recurringOptions, settingOptions } =
    useCalendarEvent()

  const { data: defaultServiceTypeData } =
    useServiceTypeControllerGetDefaultServiceType(
      { clientId: currentEvent?.patient?.id || '' },
      {
        query: {
          enabled: !!currentEvent?.patient?.id
        }
      }
    )

  const validationSchema = Yup.object().shape({
    eventType: Yup.string()
      .oneOf(Object.values(EventType))
      .required('Event type is required'),
    patientId: Yup.string().uuid(),
    subject: Yup.string().required('Subject is required'),
    serviceType: Yup.object()
      .shape({
        value: Yup.string().required(),
        label: Yup.string().required()
      })
      .required('Service type is required'),
    eventDate: Yup.date().required('Event date is required'),
    eventStartTime: Yup.string().required('Event start time is required'),
    eventEndTime: Yup.string()
      .required('Event end time is required')
      .test(
        'is-greater',
        'Event end time should be greater than start time',
        function (value) {
          const { eventStartTime } = this.parent
          return moment(value, 'hh:mm a').isSameOrAfter(
            moment(eventStartTime, 'hh:mm a')
          )
        }
      ),
    setting: Yup.string()
      .oneOf(Object.values(EVENT_SETTING_OPTIONS))
      .required('Setting is required'),
    recurring: Yup.string().required('Recurring is required'),
    occurrenceCount: Yup.number()
      .min(1, 'Occurrence count must be at least 1')
      .when('recurring', (recurring: string, schema: Yup.NumberSchema) => {
        return recurring !== 'no-repeat'
          ? schema.required('Occurrence count is required for recurring events')
          : schema.notRequired()
      })
  })

  if (!currentEvent) {
    return null
  }

  const getOptionByValue = (
    options: { value: string; label: string }[],
    value: string | undefined
  ) => {
    if (!value) {
      return null
    }
    return options.find(option => option.value === value)
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={
        {
          eventType: currentEvent.eventType,
          patientId: currentEvent.patient?.id,
          subject: currentEvent.subject || '',
          serviceType: getOptionByValue(
            serviceTypeOptions,
            currentEvent.serviceType?.code ||
              defaultServiceTypeData?.defaultServiceType?.code
          ),
          eventDate: moment(currentEvent.start).toDate(),
          eventStartTime: moment(currentEvent.start).format('hh:mm a'),
          eventEndTime: moment(currentEvent.end).format('hh:mm a'),
          setting: currentEvent.setting,
          recurring: 'no-repeat',
          occurrenceCount: 1,
          reminders: true,
          description: currentEvent.notes || ''
        } as AddEditEventFormValues
      }
      validationSchema={validationSchema}
      onSubmit={values => {
        onSubmit(values)
      }}
    >
      {(formikProps: FormikProps<AddEditEventFormValues>) => {
        const { isValid, dirty } = formikProps
        return (
          <Form>
            <ModalContent>
              <ModalBody p="12px 24px 0px 24px">
                <Tabs
                  variant="unstyled"
                  defaultIndex={
                    currentEvent.eventType === EventType.CLIENT_SESSION ? 0 : 1
                  }
                >
                  <TabList gap="16px">
                    <FancyTab
                      onClick={() => {
                        if (
                          currentEvent.eventType !== EventType.CLIENT_SESSION
                        ) {
                          onSwitchEvent(EventType.CLIENT_SESSION)
                        }
                        return
                      }}
                    >
                      <HStack>
                        <Person color="primary" />
                        <Text ml="4px" fontSize="14px">
                          Client session
                        </Text>
                      </HStack>
                    </FancyTab>
                    <FancyTab
                      onClick={() => {
                        if (currentEvent.eventType !== EventType.OTHER_EVENT) {
                          onSwitchEvent(EventType.OTHER_EVENT)
                        }
                        return
                      }}
                    >
                      Other event
                    </FancyTab>
                  </TabList>
                </Tabs>
                {currentEvent.eventType === EventType.CLIENT_SESSION && (
                  <ClientSessionForm
                    onEditClient={onEditClient}
                    formikProps={formikProps}
                    serviceTypeOptions={serviceTypeOptions}
                    settingOptions={settingOptions}
                    recurringOptions={recurringOptions}
                  />
                )}
                {currentEvent.eventType === EventType.OTHER_EVENT && (
                  <OtherEventForm
                    formikProps={formikProps}
                    settingOptions={settingOptions}
                    recurringOptions={recurringOptions}
                  />
                )}
              </ModalBody>
              <EventModalFooter gap="10px">
                <Button
                  type="button"
                  variant="outline"
                  borderRadius="8px"
                  onClick={onCancel}
                  m="0px"
                  flex="1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  borderRadius="8px"
                  m="0px"
                  flex="1"
                  isDisabled={!isValid || (!dirty && !!currentEvent?.id)}
                >
                  Save
                </Button>
              </EventModalFooter>
            </ModalContent>
          </Form>
        )
      }}
    </Formik>
  )
}
